import {
  Component,
  EventEmitter,
  inject,
  OnInit,
  OnDestroy,
  Output,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RecordDetailsComponent } from '../../../../shared/components/record-details/record-details.component';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { LeadsService } from '../../../leads/services/leads.service';
import { Company } from '../../../../shared/interfaces/company';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { takeUntil, forkJoin } from 'rxjs';
import { LeadStatusValues } from '../../../leads/models/lead-status';
import { LeadStatusUpdatePayload } from '../../../../shared/interfaces/lead_status';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';

@Component({
  selector: 'app-update-leads-popup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RecordDetailsComponent,
    PaginationComponent,
  ],
  templateUrl: './update-leads-popup.component.html',
  styleUrl: './update-leads-popup.component.scss',
})
export class UpdateLeadsPopupComponent
  extends DestroyBase
  implements OnInit, OnDestroy
{
  @Input() showCloseButton = false;
  @Output() closed = new EventEmitter<void>();

  private leadsService = inject(LeadsService);
  private commonService = inject(CommonService);

  private originalBodyOverflow = '';

  totalLeads = 0;
  pendingLeads: Company[] = [];
  noStatusLeads: Company[] = [];
  outdatedStatusLeads: Company[] = [];
  pendingLeadsCount = 0;
  currentPage = 1;
  pageSize = 10;

  noStatusCurrentPage = 1;
  noStatusPageSize = 10;
  noStatusLeadsCount = 0;

  outdatedStatusCurrentPage = 1;
  outdatedStatusPageSize = 10;
  outdatedStatusLeadsCount = 0;

  isLoadingMoreNoStatus = false;
  isLoadingMoreOutdated = false;

  get hasMoreNoStatusLeads(): boolean {
    return this.noStatusLeads.length < this.noStatusLeadsCount;
  }

  get hasMoreOutdatedStatusLeads(): boolean {
    return this.outdatedStatusLeads.length < this.outdatedStatusLeadsCount;
  }
  isNoStatusSectionCollapsed = false;
  isOutdatedStatusSectionCollapsed = false;

  isNavigating = false;
  isLoadingLeads = true;
  isSaving = false;
  showRecordDetails = false;
  selectedLeadUniqueId: string | null = null;
  statusChanges = new Map<string, string>(); // Map of unique_id to new status

  activeTab: 'no_status' | 'outdated' = 'no_status';

  setActiveTab(tab: 'no_status' | 'outdated'): void {
    this.activeTab = tab;
  }

  getNoStatusCompletedCount(): number {
    return this.noStatusLeads.filter((lead) =>
      this.statusChanges.has(lead.unique_id),
    ).length;
  }

  getNoStatusProgressPercentage(): number {
    if (this.noStatusLeadsCount === 0) return 100;
    const completed = this.getNoStatusCompletedCount();
    return Math.min(
      100,
      Math.round((completed / this.noStatusLeadsCount) * 100),
    );
  }

  getOutdatedStatusCompletedCount(): number {
    return this.outdatedStatusLeads.filter((lead) =>
      this.statusChanges.has(lead.unique_id),
    ).length;
  }

  getOutdatedStatusProgressPercentage(): number {
    if (this.outdatedStatusLeadsCount === 0) return 100;
    const completed = this.getOutdatedStatusCompletedCount();
    return Math.min(
      100,
      Math.round((completed / this.outdatedStatusLeadsCount) * 100),
    );
  }

  getTotalCompletedCount(): number {
    return this.statusChanges.size;
  }

  getTotalProgressPercentage(): number {
    if (this.pendingLeadsCount === 0) return 100;
    return Math.min(
      100,
      Math.round((this.statusChanges.size / this.pendingLeadsCount) * 100),
    );
  }

  leadStatusOptions = [
    {
      option: 'Under review',
      value: LeadStatusValues['under_review'],
    },
    {
      option: 'Further information required',
      value: LeadStatusValues['further_information_required'],
    },
    {
      option: 'Prospective client',
      value: LeadStatusValues['prospective_client'],
    },
    {
      option: 'On hold',
      value: LeadStatusValues['on_hold'],
    },
    {
      option: 'Declined',
      value: LeadStatusValues['declined'],
    },
    {
      option: 'Not currently pursuing',
      value: LeadStatusValues['not_currently_pursuing'],
    },
    {
      option: 'Active client',
      value: LeadStatusValues['active_client'],
    },
    {
      option: 'Request lead contact',
      value: LeadStatusValues['request_lead_contact'],
    },
    {
      option: 'Active Funded',
      value: LeadStatusValues['active_funded'],
    },
    {
      option: 'Active Funded Grant',
      value: LeadStatusValues['active_funded_grant'],
    },
    {
      option: 'Active Technical Assistance',
      value: LeadStatusValues['active_technical_assistance'],
    },
  ];

  approvedLoanAmounts: Record<string, number | null | string> = {};

  fundedStatuses = [
    LeadStatusValues['active_funded'],
    LeadStatusValues['active_funded_grant'],
  ];

  isFundedStatus(value: string | null | undefined): boolean {
    return !!value && this.fundedStatuses.includes(value);
  }

  getApprovedAmountLabel(lead: Company): string {
    const status = this.getSelectedStatusValue(lead);
    return status === LeadStatusValues['active_funded_grant']
      ? 'Approved Grant Amount'
      : 'Approved Loan Amount';
  }

  getApprovedAmountError(lead: Company): string | null {
    const value = this.approvedLoanAmounts[lead.unique_id];
    const label = this.getApprovedAmountLabel(lead);

    if (value === null || value === undefined || value === '') {
      return `${label} is required.`;
    }

    const str = value.toString();
    const num = Number(str);

    // 2. Invalid number
    if (isNaN(num)) {
      return 'Invalid number.';
    }

    // 3. 0 or negative
    if (num <= 0) {
      return 'Amount must be greater than 0.';
    }

    const parts = str.split('.');
    const integerPart = parts[0].replace(/^-/, '');
    const decimalPart = parts[1] || '';

    if (integerPart.length > 10) {
      return `${label} must not exceed 10 whole digits.`;
    }

    if (decimalPart.length > 2) {
      return `${label} must not exceed 2 decimal places.`;
    }

    return null;
  }

  shouldShowApprovedAmountError(lead: Company): boolean {
    const status = this.getSelectedStatusValue(lead);
    return this.isFundedStatus(status) && !!this.getApprovedAmountError(lead);
  }

  hasFundedAmountErrors(): boolean {
    return this.pendingLeads.some((lead) =>
      this.shouldShowApprovedAmountError(lead),
    );
  }

  ngOnInit(): void {
    this.disableBackgroundScroll();
    this.loadPendingLeads();
  }

  override ngOnDestroy(): void {
    this.enableBackgroundScroll();
    super.ngOnDestroy();
  }

  private disableBackgroundScroll(): void {
    this.originalBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  private enableBackgroundScroll(): void {
    document.body.style.overflow = this.originalBodyOverflow;
  }

  private loadPendingLeads(): void {
    this.isLoadingLeads = true;

    forkJoin([
      // Case 1: Leads without any status (API 1: lead_status=0)
      this.leadsService.getNoStatusLeads(
        this.noStatusCurrentPage,
        this.noStatusPageSize,
      ),
      // Case 2: Leads with outdated status (API 2: is_reminder=true)
      this.leadsService.getOutdatedStatusLeads(
        this.outdatedStatusCurrentPage,
        this.outdatedStatusPageSize,
      ),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([noStatusRes, outdatedStatusRes]) => {
          this.noStatusLeads = noStatusRes?.results || [];
          this.noStatusLeadsCount = noStatusRes?.count || 0;

          this.outdatedStatusLeads = outdatedStatusRes?.results || [];
          this.outdatedStatusLeadsCount = outdatedStatusRes?.count || 0;

          this.updatePendingLeadsList();

          if (
            this.noStatusLeadsCount === 0 &&
            this.outdatedStatusLeadsCount > 0
          ) {
            this.activeTab = 'outdated';
          } else {
            this.activeTab = 'no_status';
          }

          this.isLoadingLeads = false;
        },
        error: (error) => {
          console.error('Failed to load pending leads:', error);
          this.pendingLeads = [];
          this.noStatusLeads = [];
          this.outdatedStatusLeads = [];
          this.noStatusLeadsCount = 0;
          this.outdatedStatusLeadsCount = 0;
          this.pendingLeadsCount = 0;
          this.totalLeads = 0;
          this.isLoadingLeads = false;
        },
      });
  }

  loadNoStatusLeads(): void {
    this.leadsService
      .getNoStatusLeads(this.noStatusCurrentPage, this.noStatusPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.noStatusLeads = res?.results || [];
          this.noStatusLeadsCount = res?.count || 0;
          this.updatePendingLeadsList();
        },
        error: (err) => {
          console.error('Failed to load no-status leads:', err);
        },
      });
  }

  loadOutdatedStatusLeads(): void {
    this.leadsService
      .getOutdatedStatusLeads(
        this.outdatedStatusCurrentPage,
        this.outdatedStatusPageSize,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.outdatedStatusLeads = res?.results || [];
          this.outdatedStatusLeadsCount = res?.count || 0;
          this.updatePendingLeadsList();
        },
        error: (err) => {
          console.error('Failed to load outdated-status leads:', err);
        },
      });
  }

  loadMoreNoStatusLeads(): void {
    if (this.isLoadingMoreNoStatus || !this.hasMoreNoStatusLeads) return;
    this.isLoadingMoreNoStatus = true;
    this.noStatusCurrentPage++;

    this.leadsService
      .getNoStatusLeads(this.noStatusCurrentPage, this.noStatusPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.noStatusLeads = [...this.noStatusLeads, ...(res?.results || [])];
          this.noStatusLeadsCount = res?.count || this.noStatusLeadsCount;
          this.updatePendingLeadsList();
          this.isLoadingMoreNoStatus = false;
        },
        error: (err) => {
          console.error('Failed to load more no-status leads:', err);
          this.isLoadingMoreNoStatus = false;
        },
      });
  }

  loadMoreOutdatedStatusLeads(): void {
    if (this.isLoadingMoreOutdated || !this.hasMoreOutdatedStatusLeads) return;
    this.isLoadingMoreOutdated = true;
    this.outdatedStatusCurrentPage++;

    this.leadsService
      .getOutdatedStatusLeads(
        this.outdatedStatusCurrentPage,
        this.outdatedStatusPageSize,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.outdatedStatusLeads = [
            ...this.outdatedStatusLeads,
            ...(res?.results || []),
          ];
          this.outdatedStatusLeadsCount =
            res?.count || this.outdatedStatusLeadsCount;
          this.updatePendingLeadsList();
          this.isLoadingMoreOutdated = false;
        },
        error: (err) => {
          console.error('Failed to load more outdated-status leads:', err);
          this.isLoadingMoreOutdated = false;
        },
      });
  }

  onContentScroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target) return;

    const threshold = 100;
    const atBottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + threshold;

    if (atBottom) {
      if (this.activeTab === 'no_status' && this.hasMoreNoStatusLeads) {
        this.loadMoreNoStatusLeads();
      } else if (
        this.activeTab === 'outdated' &&
        this.hasMoreOutdatedStatusLeads
      ) {
        this.loadMoreOutdatedStatusLeads();
      }
    }
  }

  private updatePendingLeadsList(): void {
    this.pendingLeads = [...this.noStatusLeads, ...this.outdatedStatusLeads];
    this.pendingLeadsCount =
      this.noStatusLeadsCount + this.outdatedStatusLeadsCount;
    this.totalLeads = this.pendingLeadsCount;
  }

  onStatusChange(uniqueId: string, event: Event): void {
    const target = event.target as HTMLSelectElement;
    if (!target || !uniqueId) return;

    const value = target.value;
    this.statusChanges.set(uniqueId, value);

    // Clear approved amount if switching to non-funded
    if (!this.isFundedStatus(value)) {
      this.approvedLoanAmounts[uniqueId] = null;
    }
  }

  saveChanges(): void {
    if (this.isSaving || this.statusChanges.size === 0) return;

    this.isSaving = true;
    const updatedCount = this.statusChanges.size;

    // Validate before sending
    for (const [unique_id, lead_status] of this.statusChanges.entries()) {
      if (
        this.isFundedStatus(lead_status) &&
        !this.approvedLoanAmounts[unique_id]
      ) {
        this.isSaving = false;
        return;
      }
    }

    const updates: LeadStatusUpdatePayload[] = Array.from(
      this.statusChanges.entries(),
    ).map(([unique_id, lead_status]) => {
      const payload: LeadStatusUpdatePayload = {
        unique_id,
        lead_status,
      };

      if (this.isFundedStatus(lead_status)) {
        const rawAmount = this.approvedLoanAmounts[unique_id];
        const amount =
          typeof rawAmount === 'string' ? Number(rawAmount) : rawAmount;
        payload.approved_loan_amount =
          amount !== null && amount !== undefined && !Number.isNaN(amount)
            ? amount
            : null;
      }

      return payload;
    });

    this.leadsService
      .updateLeadStatus(updates)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.statusChanges.clear();

          // Trigger refresh of leads status counts in dashboard
          this.leadsService.triggerStatusCountRefresh();

          // Check if all pending leads were updated
          if (updatedCount >= this.pendingLeadsCount) {
            // User updated all pending leads
            this.displayToast(
              `Successfully updated ${updatedCount} lead${updatedCount > 1 ? 's' : ''}!`,
              'success',
            );
          } else {
            // User updated only some leads
            this.displayToast(
              `Successfully updated ${updatedCount} lead${updatedCount > 1 ? 's' : ''}!`,
              'success',
            );
          }
          this.remindLater();
        },
        error: (error) => {
          console.error('Failed to update lead statuses:', error);
          this.displayToast('Failed to update lead statuses', 'error');
          this.isSaving = false;
        },
      });
  }

  remindLater(): void {
    if (this.isNavigating) return;

    // Store dismiss timestamp for 48 hours
    const dismissUntil = new Date();
    dismissUntil.setHours(dismissUntil.getHours() + 48);
    localStorage.setItem(
      'leadsStatusPopupDismissedUntil',
      dismissUntil.toISOString(),
    );

    this.closePopup();
  }

  onAssignUnassignLead(company: Company | null): void {
    if (company && company.assigned_to === null) {
      // Lead was unassigned
      this.pendingLeads = this.pendingLeads.filter(
        (lead) => lead.unique_id !== company!.unique_id,
      );
      this.noStatusLeads = this.noStatusLeads.filter(
        (lead) => lead.unique_id !== company!.unique_id,
      );
      this.outdatedStatusLeads = this.outdatedStatusLeads.filter(
        (lead) => lead.unique_id !== company!.unique_id,
      );
      this.pendingLeadsCount--;
      this.totalLeads--;
    } else if (company && company.assigned_to !== null) {
      // Lead was assigned
      this.pendingLeads.push(company);
      // Categorize the new lead
      if (!company.lead_status || company.lead_status === '') {
        this.noStatusLeads.push(company);
      } else {
        this.outdatedStatusLeads.push(company);
      }
      this.pendingLeadsCount++;
      this.totalLeads++;
    }
  }

  onLeadStatusUpdated(event: { uniqueId: string; newStatus: string }): void {
    const leadIndex = this.pendingLeads.findIndex(
      (lead) => lead.unique_id === event.uniqueId,
    );

    if (event.newStatus === '' || event.newStatus === null) {
      // Lead status was set to empty/not defined - add it back if not already in the list
      if (leadIndex === -1) {
        // Need to fetch the lead details to add it back
        // For now, we'll reload the pending leads to include this one
        this.loadPendingLeads();
        this.displayToast('Lead status updated successfully!', 'success');
      }
    } else {
      // Lead status was set to a valid value - remove from pending list
      if (leadIndex !== -1) {
        this.pendingLeads.splice(leadIndex, 1);

        // Remove from category arrays
        const noStatusIndex = this.noStatusLeads.findIndex(
          (lead) => lead.unique_id === event.uniqueId,
        );
        if (noStatusIndex !== -1) {
          this.noStatusLeads.splice(noStatusIndex, 1);
        }

        const outdatedStatusIndex = this.outdatedStatusLeads.findIndex(
          (lead) => lead.unique_id === event.uniqueId,
        );
        if (outdatedStatusIndex !== -1) {
          this.outdatedStatusLeads.splice(outdatedStatusIndex, 1);
        }

        this.pendingLeadsCount--;
        this.totalLeads--;

        // Also remove from statusChanges map if it exists
        this.statusChanges.delete(event.uniqueId);

        // Show success toast
        this.displayToast('Lead status updated successfully!', 'success');
      }
    }
  }

  openRecordDetails(uniqueId: string): void {
    this.selectedLeadUniqueId = uniqueId;
    this.showRecordDetails = true;
  }

  closePopup(): void {
    this.enableBackgroundScroll();
    this.closed.emit();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadPendingLeads();
  }

  onRecordDetailsClosed(): void {
    this.showRecordDetails = false;
    this.selectedLeadUniqueId = null;
    if (this.pendingLeadsCount === 0) {
      this.leadsService.triggerStatusCountRefresh();
      this.closePopup();
      return;
    }
    // Use setTimeout to ensure this runs after SecondaryPopupComponent's ngOnDestroy
    setTimeout(() => {
      this.disableBackgroundScroll();
    }, 0);
  }

  private displayToast(message: string, type: 'success' | 'error'): void {
    this.commonService.triggerToast({
      type: type === 'success' ? ToastType.Success : ToastType.Error,
      message,
    });
  }

  getStatusDisplayValue(statusValue: string | undefined): string {
    if (!statusValue) return '';
    const status = this.leadStatusOptions.find((s) => s.value === statusValue);
    return status ? status.option : statusValue;
  }

  formatDate(dateString: Date | undefined): string {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} day(s) ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week(s) ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} month(s) ago`;
    return `${Math.floor(diffDays / 365)} year(s) ago`;
  }

  toggleNoStatusSection(): void {
    this.isNoStatusSectionCollapsed = !this.isNoStatusSectionCollapsed;
  }

  toggleOutdatedStatusSection(): void {
    this.isOutdatedStatusSectionCollapsed =
      !this.isOutdatedStatusSectionCollapsed;
  }

  getSelectedStatusValue(lead: Company): string {
    // First check if there's a pending change for this lead
    const pendingChange = this.statusChanges.get(lead.unique_id);
    if (pendingChange) {
      return pendingChange;
    }
    // Otherwise return the current status or empty string
    return lead.lead_status || '';
  }
}
